import React, { useCallback, useMemo, useState } from 'react'
import { Button } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { Icon } from '#components/Icon'
import type { TAnnouncementVariant } from '#interfaces/announcement'
import type { ICssCustomProperties } from '#interfaces/cssCustomProperties'

import type { IProps } from './interfaces'
import styles from './styles.module.scss'

const CHARS_PER_SECOND = 40
const MIN_DURATION_SECONDS = 10

const SEVERITY_RANK: Record<TAnnouncementVariant, number> = {
  critical: 3,
  warning: 2,
  info: 1,
}

const SEVERITY_ATTR: Record<TAnnouncementVariant, 'danger' | 'warning' | 'info'> = {
  critical: 'danger',
  warning: 'warning',
  info: 'info',
}

const highestSeverity = (variants: TAnnouncementVariant[]): TAnnouncementVariant => {
  let highest: TAnnouncementVariant = 'info'

  for (const variant of variants) if (SEVERITY_RANK[variant] > SEVERITY_RANK[highest]) highest = variant

  return highest
}

export const AnnouncementMarquee = ({ announcements }: IProps): React.JSX.Element | null => {
  const [isPaused, setIsPaused] = useState<boolean>(false)
  const { t } = useTranslation()

  const severity = useMemo(
    (): TAnnouncementVariant => highestSeverity(announcements.map((item) => item.variant)),
    [announcements],
  )

  const durationSeconds = useMemo((): number => {
    const totalChars = announcements.reduce((sum, item) => sum + item.message.length, 0)
    return Math.max(totalChars / CHARS_PER_SECOND, MIN_DURATION_SECONDS)
  }, [announcements])

  const handleToggle = useCallback((): void => {
    setIsPaused((current) => !current)
  }, [])

  const marqueeStyle: ICssCustomProperties = {
    '--marquee-duration': `${durationSeconds}s`,
  }

  if (announcements.length === 0) return null

  const renderTrack = (hidden: boolean): React.JSX.Element => (
    <div className={styles.track} aria-hidden={hidden ? true : undefined}>
      {announcements.map((item, index) => (
        <React.Fragment key={`${hidden ? 'dup' : 'live'}-${item.id}`}>
          {index > 0 && (
            <span className={styles.bullet} aria-hidden="true">
              •
            </span>
          )}
          <span className={styles.message}>{item.message}</span>
        </React.Fragment>
      ))}
    </div>
  )

  return (
    <div
      className={styles.marquee}
      role="status"
      data-testid="announcement-marquee"
      data-severity={SEVERITY_ATTR[severity]}
      data-paused={isPaused ? 'true' : 'false'}
      style={marqueeStyle}
    >
      <div className={styles.viewport}>
        {renderTrack(false)}
        {renderTrack(true)}
      </div>
      <Button
        className={styles.toggle}
        onPress={handleToggle}
        aria-label={isPaused ? t('Play announcements') : t('Pause announcements')}
        data-testid="announcement-marquee-toggle"
      >
        <Icon name={isPaused ? 'play' : 'pause'} width="16" height="16" />
      </Button>
    </div>
  )
}
